import { z } from 'zod';

/**
 * Contrats des amis (spec 09, ADR 0019 — amitié MUTUELLE : demande →
 * acceptation). Aucune donnée privée : uniquement ce que le profil public
 * expose. ⚠️ Le statut `declined` n'apparaît dans AUCUN contrat sortant
 * (anti-harcèlement, spec 09 §3.2) : un refus est indistinguable d'une
 * absence de réponse pour le demandeur.
 */

/** Cap anti-abus : nombre maximal d'amitiés `accepted` par compte (spec 09 §3.2). */
export const FRIENDS_MAX = 500;
/** Cap anti-abus : nombre maximal de demandes `pending` SORTANTES par compte. */
export const FRIEND_REQUESTS_MAX = 100;
/** Anti-spam par paire : créations de `pending` vers la MÊME personne / 24 h. */
export const FRIEND_REQUEST_PAIR_DAILY_MAX = 3;
/** Longueur minimale de la recherche par pseudo (anti-scan). */
export const FRIENDS_SEARCH_MIN = 2;
/** Nombre maximal de résultats de recherche. */
export const FRIENDS_SEARCH_LIMIT = 10;

/**
 * Relation entre MOI (le viewer) et un autre compte — vue du demandeur.
 * ⚠️ Un `declined` A→B se présente : à A comme `pending_out` (le refus est
 * invisible), à B comme `none` (sa boîte de réception ne le montre plus).
 */
export type FriendRelation = 'none' | 'friend' | 'pending_out' | 'pending_in';

/** Extrait de profil PUBLIC d'un compte (jamais rien de privé). */
export interface FriendDto {
  id: string;
  displayName: string;
  avatarId: string | null;
}

/** Un ami accepté. `since` = date d'acceptation (ISO). */
export interface FriendListItemDto extends FriendDto {
  since: string;
}

/** Une demande (reçue ou envoyée). `at` = date d'envoi (ISO). */
export interface FriendRequestItemDto extends FriendDto {
  at: string;
}

/**
 * Mes amis + demandes (spec 09 §4). `outgoing` mélange `pending` et `declined`
 * présentés À L'IDENTIQUE (aucun champ ne trahit un refus).
 */
export interface FriendsListDto {
  friends: FriendListItemDto[];
  incoming: FriendRequestItemDto[];
  outgoing: FriendRequestItemDto[];
}

/** Résultat de recherche : profil public + relation avec moi. */
export interface FriendSearchItemDto extends FriendDto {
  relation: FriendRelation;
}

export interface FriendSearchDto {
  results: FriendSearchItemDto[];
}

/** Réponse des mutations (demande/accept/decline) : ma relation résultante. */
export interface FriendRelationDto {
  relation: FriendRelation;
}

/** Demande d'ami : par id de compte (stable — le pseudo peut changer). */
export const FriendRequestSchema = z.object({ userId: z.string().cuid() }).strict();
export type FriendRequestInput = z.infer<typeof FriendRequestSchema>;

/** Requête de recherche (le contrôleur valide `q` avant de toucher la base). */
export const FriendSearchQuerySchema = z
  .object({ q: z.string().trim().min(FRIENDS_SEARCH_MIN).max(30) })
  .strict();
export type FriendSearchQueryInput = z.infer<typeof FriendSearchQuerySchema>;
